// Users Service - Gestión de perfiles de usuario
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  increment,
  runTransaction,
  type Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import type { User } from 'firebase/auth';

export type UserRole = 'admin' | 'user';

export interface UserProfile {
  id: string;
  email: string;
  displayName?: string;
  photoURL?: string;
  termsAcceptedAt?: Timestamp;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  // Campo de rol: solo se puede asignar desde Firebase Console.
  // Ausencia del campo = usuario regular.
  role?: UserRole;
  // Campo de estado: admin puede activar/desactivar usuarios.
  // Ausencia del campo = usuario activo (retrocompatibilidad).
  active?: boolean;
  preferences?: {
    theme?: 'light' | 'dark' | 'auto';
    language?: string;
    notifications?: boolean;
  };
  stats?: {
    totalWorkflows: number;
    totalExecutions: number;
    totalSuccess: number;
    totalFailures: number;
  };
}

export interface CreateUserProfileInput {
  email: string;
  displayName?: string;
  photoURL?: string;
}

/**
 * Valida y construye un objeto UserProfile a partir de datos de Firestore.
 * [H-B018/H-F009] Validacion defensiva campo por campo — sin cast inseguro con `as`.
 */
function parseUserProfile(id: string, data: Record<string, unknown>): UserProfile {
  // Validar preferences campo por campo en lugar de cast directo
  const rawPrefs = data.preferences;
  let preferences: UserProfile['preferences'] | undefined;
  if (rawPrefs && typeof rawPrefs === 'object' && !Array.isArray(rawPrefs)) {
    const p = rawPrefs as Record<string, unknown>;
    preferences = {
      theme: (p.theme === 'light' || p.theme === 'dark' || p.theme === 'auto') ? p.theme : undefined,
      language: typeof p.language === 'string' ? p.language : undefined,
      notifications: typeof p.notifications === 'boolean' ? p.notifications : undefined,
    };
  }

  // Validar stats campo por campo en lugar de cast directo
  const rawStats = data.stats;
  let stats: UserProfile['stats'] | undefined;
  if (rawStats && typeof rawStats === 'object' && !Array.isArray(rawStats)) {
    const s = rawStats as Record<string, unknown>;
    stats = {
      totalWorkflows: typeof s.totalWorkflows === 'number' ? s.totalWorkflows : 0,
      totalExecutions: typeof s.totalExecutions === 'number' ? s.totalExecutions : 0,
      totalSuccess: typeof s.totalSuccess === 'number' ? s.totalSuccess : 0,
      totalFailures: typeof s.totalFailures === 'number' ? s.totalFailures : 0,
    };
  }

  return {
    id,
    email: typeof data.email === 'string' ? data.email : '',
    displayName: typeof data.displayName === 'string' ? data.displayName : undefined,
    photoURL: typeof data.photoURL === 'string' ? data.photoURL : undefined,
    termsAcceptedAt: data.termsAcceptedAt ? (data.termsAcceptedAt as Timestamp) : undefined,
    createdAt: data.createdAt as Timestamp,
    updatedAt: data.updatedAt as Timestamp,
    // Validar campo role: solo valores conocidos, ausencia = undefined (usuario regular)
    // Normalizar: trim + lowercase para tolerar "Admin", " admin ", etc.
    role: (() => {
      const raw = typeof data.role === 'string' ? data.role.trim().toLowerCase() : '';
      return (raw === 'admin' || raw === 'user') ? raw as UserRole : undefined;
    })(),
    // Validar campo active: boolean o undefined (ausencia = activo)
    active: typeof data.active === 'boolean' ? data.active : undefined,
    preferences,
    stats,
  };
}

class UsersService {
  private getUserDoc(userId: string) {
    return doc(db, 'users', userId);
  }

  // Crear perfil de usuario al registrarse
  // [H-B008] Usa runTransaction para atomic check+write, eliminando condicion de carrera.
  async createUserProfile(user: User, input?: CreateUserProfileInput): Promise<void> {
    const userDoc = this.getUserDoc(user.uid);

    await runTransaction(db, async (transaction) => {
      const existingProfile = await transaction.get(userDoc);
      if (existingProfile.exists()) {
        return; // Perfil ya existe, no crear duplicado
      }

      // Construir objeto de datos, excluyendo campos undefined
      const userData: Record<string, unknown> = {
        email: input?.email || user.email || '',
        termsAcceptedAt: serverTimestamp(),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        preferences: {
          theme: 'auto',
          language: 'es',
          notifications: true,
        },
        stats: {
          totalWorkflows: 0,
          totalExecutions: 0,
          totalSuccess: 0,
          totalFailures: 0,
        },
      };

      // Solo agregar displayName si tiene valor
      const displayName = input?.displayName || user.displayName;
      if (displayName) {
        userData.displayName = displayName;
      }

      // Solo agregar photoURL si tiene valor
      const photoURL = input?.photoURL || user.photoURL;
      if (photoURL) {
        userData.photoURL = photoURL;
      }

      transaction.set(userDoc, userData);
    });
  }

  // Obtener perfil de usuario
  async getUserProfile(userId: string): Promise<UserProfile | null> {
    const userDoc = this.getUserDoc(userId);
    const docSnap = await getDoc(userDoc);

    if (!docSnap.exists()) {
      return null;
    }

    return parseUserProfile(docSnap.id, docSnap.data() as Record<string, unknown>);
  }

  // Actualizar perfil de usuario
  // [H-B003] Whitelist de campos permitidos para prevenir escritura de campos arbitrarios.
  async updateUserProfile(userId: string, updates: Partial<Omit<UserProfile, 'id' | 'createdAt'>>): Promise<void> {
    const userDoc = this.getUserDoc(userId);
    const allowedFields = ['displayName', 'photoURL', 'preferences'];
    const filtered = Object.fromEntries(
      Object.entries(updates).filter(([key]) => allowedFields.includes(key))
    );

    // Solo actualizar si hay campos validos
    if (Object.keys(filtered).length === 0) return;

    await updateDoc(userDoc, {
      ...filtered,
      updatedAt: serverTimestamp(),
    });
  }

  // Actualizar estadísticas del usuario
  // [H-B004] Usa increment() atomico para eliminar condicion de carrera read-then-write.
  // Cada campo se actualiza con notacion de punto para operaciones atomicas independientes.
  async updateUserStats(userId: string, deltas: { totalWorkflows?: number; totalExecutions?: number; totalSuccess?: number; totalFailures?: number }): Promise<void> {
    const userDoc = this.getUserDoc(userId);
    const updateData: Record<string, unknown> = {
      updatedAt: serverTimestamp(),
    };

    if (deltas.totalWorkflows !== undefined) {
      updateData['stats.totalWorkflows'] = increment(deltas.totalWorkflows);
    }
    if (deltas.totalExecutions !== undefined) {
      updateData['stats.totalExecutions'] = increment(deltas.totalExecutions);
    }
    if (deltas.totalSuccess !== undefined) {
      updateData['stats.totalSuccess'] = increment(deltas.totalSuccess);
    }
    if (deltas.totalFailures !== undefined) {
      updateData['stats.totalFailures'] = increment(deltas.totalFailures);
    }

    await updateDoc(userDoc, updateData);
  }
}

export const usersService = new UsersService();

